import { issuePriorities, kittens } from '@atlassian/jira-mocks-assets-collection';
import { PAGE_SIZE } from './constants';
import type {
	Priority,
	PaginatedPrioritiesData,
	AvatarResponseObject,
	CropProperties,
	GetAvatarsResponse,
	PostAvatarResponse,
	CreateAndEditPriorityRequestBody,
} from './types';

export const generateMockPriorities = (startAt: number, numberOfItems: number): Priority[] =>
	new Array(numberOfItems).fill({}).map((_, index) => {
		const id = startAt + 1 + index;
		return {
			statusColor: '#123456',
			description: `Mock priority #${id} description`,
			avatarId: `${id}`,
			iconUrl: issuePriorities.major,
			name: `Priority #${id}`,
			id: `${id}`,
			isDefault: true,
			schemes: {
				isLast: true,
				maxResults: 15,
				startAt: 0,
				total: 1,
				values: [
					{
						id: '10000',
						name: 'Test priority scheme',
						self: 'https://test-instance.jira-dev.com/rest/api/3/priorityscheme/10000',
					},
				],
			},
		};
	});

const DEFAULT_TOTAL_RESULTS = 75; // 2.5 pages

/**
 * This is the expected response from the API, where avatarId
 * is a number. We can use the refine helper function to get the
 * exact type.
 * @param pageNumber
 * @param total
 * @returns
 */
export const generateMockSearchPrioritiesAPIResponse = (
	pageNumber: number,
	total: number = DEFAULT_TOTAL_RESULTS,
) => {
	const startAt = (pageNumber - 1) * PAGE_SIZE;
	const isLast = pageNumber * PAGE_SIZE >= total;
	const values = generateMockPriorities(
		startAt,
		isLast ? total % PAGE_SIZE || PAGE_SIZE : PAGE_SIZE,
	).map((obj) => ({ ...obj, avatarId: Number(obj.avatarId) }));

	return {
		maxResults: PAGE_SIZE,
		startAt,
		total,
		isLast,
		values,
	};
};

export const generateMockPaginatedPrioritiesData = ({
	currentPage,
	totalItems,
}: {
	currentPage: number;
	totalItems: number;
}): PaginatedPrioritiesData => {
	const totalPages = totalItems / PAGE_SIZE;
	const hasPreviousPage = currentPage > 1;
	const hasNextPage = currentPage < totalPages;
	const itemsOnCurrentPage = hasNextPage ? PAGE_SIZE : totalItems - (currentPage - 1) * PAGE_SIZE;

	const pages = {
		[currentPage]: {
			items: generateMockPriorities((currentPage - 1) * PAGE_SIZE, itemsOnCurrentPage),
			isLast: !hasNextPage,
		},
	};
	if (hasPreviousPage) {
		pages[currentPage - 1] = {
			items: generateMockPriorities((currentPage - 2) * PAGE_SIZE, PAGE_SIZE),
			isLast: false,
		};
	}
	if (hasNextPage) {
		const isNextPageTheLastPage = currentPage + 1 < totalPages;
		const itemsOnNextPage = isNextPageTheLastPage
			? PAGE_SIZE
			: totalItems - currentPage * PAGE_SIZE;
		pages[currentPage + 1] = {
			items: generateMockPriorities(currentPage * PAGE_SIZE, itemsOnNextPage),
			isLast: isNextPageTheLastPage,
		};
	}
	return {
		pages,
		pageSize: PAGE_SIZE,
		totalItems,
	};
};

export const mockPriorityAvatars = [
	{
		id: '1',
		dataURI: kittens.kitten1,
		name: 'Kitten 1',
	},
	{
		id: '2',
		dataURI: kittens.kitten2,
		name: 'Kitten 2',
	},
	{
		id: '3',
		dataURI: kittens.kitten3,
		name: 'Kitten 3',
	},
];

export const mockPriorityWithNoSchemes = {
	self: 'https://test.jira-dev.com/rest/api/3/priority/1',
	statusColor: '#d04437',
	description: 'This is a description for a priority.',
	avatarId: '12345',
	iconUrl: 'https://test.jira-dev.com/images/icons/priorities/highest.svg',
	name: 'Highest',
	id: '1',
	isDefault: false,
	schemes: {
		startAt: 0,
		maxResults: 15,
		total: 0,
		isLast: true,
		values: [],
	},
};

export const mockPriorityWithSingleScheme = {
	self: 'https://test.jira-dev.com/rest/api/3/priority/1',
	statusColor: '#d04437',
	description: 'This is a description for a priority.',
	avatarId: '12345',
	iconUrl: 'https://test.jira-dev.com/images/icons/priorities/highest.svg',
	name: 'Highest',
	id: '1',
	isDefault: false,
	schemes: {
		startAt: 0,
		maxResults: 15,
		total: 1,
		isLast: true,
		values: [
			{
				self: 'https://test.jira-dev.com/rest/api/3/priorityscheme/10161',
				id: '10161',
				name: 'Test Priority Scheme 1',
			},
		],
	},
};

export const mockPriorityWithMultipleSchemes = {
	self: 'https://test.jira-dev.com/rest/api/3/priority/1',
	statusColor: '#d04437',
	description: 'This is a description for a priority.',
	avatarId: '12345',
	iconUrl: 'https://test.jira-dev.com/images/icons/priorities/highest.svg',
	name: 'Highest',
	id: '1',
	isDefault: false,
	schemes: {
		startAt: 0,
		maxResults: 15,
		total: 2,
		isLast: true,
		values: [
			{
				self: 'https://test.jira-dev.com/rest/api/3/priorityscheme/10161',
				id: '10161',
				name: 'Test Priority Scheme 1',
			},
			{
				self: 'https://test.jira-dev.com/rest/api/3/priorityscheme/10165',
				id: '10165',
				name: 'Test Priority Scheme 2',
			},
		],
	},
};

export const mockSystemAvatarResponseObject: AvatarResponseObject = {
	id: '12345',
	isDeletable: true,
	isSelected: true,
	isSystemAvatar: true,
	urls: {
		'16x16': issuePriorities.high,
		'24x24': issuePriorities.low,
		'32x32': issuePriorities.major,
		'48x48': issuePriorities.minor,
	},
};

export const mockCustomAvatarResponseObject: AvatarResponseObject = {
	id: '12346',
	isDeletable: true,
	isSelected: false,
	isSystemAvatar: false,
	urls: {
		'16x16': kittens.kitten1,
		'24x24': kittens.kitten2,
		'32x32': kittens.kitten3,
		'48x48': kittens.kitten4,
	},
};

export const mockGetAvatarsResponse: GetAvatarsResponse = {
	system: [mockSystemAvatarResponseObject],
	custom: [mockCustomAvatarResponseObject],
};

export const mockPostAvatarResponse: PostAvatarResponse = {
	id: '12346',
	isDeletable: true,
	isSelected: false,
	isSystemAvatar: false,
};

export const mockFile: File = new File(['(⌐□_□)'], 'chucknorris.png', { type: 'image/png' });
export const mockCropProperties: CropProperties = { x: 0, y: 0, size: 24 };

export const mockPriorityAvatarsList = [
	{
		id: mockSystemAvatarResponseObject.id,
		dataURI: mockSystemAvatarResponseObject.urls['24x24'],
	},
	{
		id: mockCustomAvatarResponseObject.id,
		dataURI: mockCustomAvatarResponseObject.urls['24x24'],
	},
];

export const mockEmptyPriority: Priority = {
	id: '',
	name: '',
	description: '',
	avatarId: '',
	iconUrl: '',
	statusColor: '',
	isDefault: false,
	schemes: {
		isLast: false,
		maxResults: 15,
		startAt: 0,
		total: 0,
		values: [],
	},
};

export const mockPriorityRequestBody: CreateAndEditPriorityRequestBody = {
	name: 'priority 1',
	description: 'a most excellent priority',
	statusColor: '#ABF5D1',
	avatarId: '1231',
};
